import Sortable from "sortablejs";



import { Factory } from "@/data/dao/Factory";
import { CatalogDao } from "@/data/dao/service/CatalogDao";
import type { ISortedArticle, ISortedCategory } from "@/data/model/Offer";
import { Format } from "@/data/util/Format";



import { translate } from "@/shared/localization/language";
import { logError } from "@/shared/logging/errors";
import { toMoneyWithSign } from "@/shared/utils/format";
import { createVueComponent } from "@/shared/utils/vue";



import { i18n } from "@/portal/Trans";
import LinkedOfferList from "@/portal/components/layout/offer/LinkedOfferList.vue";
import { UiNotification } from "@/portal/ui/Notification";


export class ArticleOverview {
    private $articleOverviewContainer: JQuery;
    private $element: JQuery;
    private $categories: JQuery;
    private offerId: string;

    private dao: CatalogDao;
    private categorySortable: any;

    constructor(offerId: string) {
        this.offerId = offerId;

        this.dao = Factory.getInstance<CatalogDao>(CatalogDao);

        this.$articleOverviewContainer = $("#linked-offers");

        if (this.$articleOverviewContainer) {
            createVueComponent(
                LinkedOfferList,
                {
                    parentOfferId: offerId,
                    onChangesApplied: () => {
                        this.loadArticles();
                        UiNotification.showSuccess(i18n("general.successfullyUpdated"));
                    },
                },
                this.$articleOverviewContainer[0],
                true
            );
        }

        this.$element = $('<div class="articleoverview" />');
        const tools = $('<div class="tools" />').appendTo(this.$element);
        const leading = $('<div class="leading" />').appendTo(tools);
        $(
            '<a href="#" class="btn btn-sm btn-secondary btn-square mr-2">' +
                i18n("elements.articleOverview.expandAll") +
                "</a>"
        )
            .appendTo(leading)
            .click((e) => {
                e.preventDefault();
                e.stopPropagation();

                this.$categories.find("> .category:not(.open)").each((i, e) => this.openCategory($(e)));
            });

        $(
            '<a href="#" class="btn btn-sm btn-secondary btn-square">' +
                i18n("elements.articleOverview.collapseAll") +
                "</a>"
        )
            .appendTo(leading)
            .click((e) => {
                e.preventDefault();
                e.stopPropagation();

                this.$categories.find("> .category.open").each((i, e) => this.closeCategory($(e)));
            });

        const trailing = $('<div class="trailing" />').appendTo(tools);
        $(
            '<a href="/offercategory/new" target="_blank" class="btn btn-sm btn-square btn-success mr-2">' +
                i18n("elements.articleOverview.newCategory") +
                "</a>"
        ).appendTo(trailing);

        $(
            '<a href="/article/new" target="_blank" class="btn btn-sm btn-success btn-square ">' +
                i18n("elements.articleOverview.newItem") +
                "</a>"
        ).appendTo(trailing);


        this.$categories = $('<div class="categories" />').appendTo(this.$element);
        this.loadArticles();
    }

    public appendTo($element: JQuery): ArticleOverview {
        $element.find("> .article-overview-container > .col-lg-9").append(this.$element);

        return this;
    }

    public loadArticles() {
        this.$categories
            .empty()
            .append(
                '<div class="loading"><div class="kt-spinner kt-spinner--success"></div> ' +
                    i18n("elements.articleOverview.loadingItems") +
                    "</div>"
            );
        this.dao
            .getOfferArticles(this.offerId)
            .then((a) => {
                this.$categories.empty();

                if (a.length === 0) {
                    this.$categories.append(
                        '<div class="nodata kt-section"><div class="kt-section__title">' +
                            i18n("elements.articleOverview.noItemsAvailable") +
                            '</div><div class="kt-section__desc">' +
                            i18n("elements.articleOverview.noItemsText") +
                            "</div></div>"
                    );
                    return;
                }

                for (const i in a) this.renderCategory(a[i]).appendTo(this.$categories);

                this.categorySortable = Sortable.create(this.$categories[0], {
                    handle: ".head > .draghandle",
                    ghostClass: "dragging",
                    animation: 150,
                    draggable: ".category",
                    onEnd: (evt) => {
                        if (evt.newIndex === evt.oldIndex) return;

                        this.onMoveCategory($(evt.item), evt.newIndex!);
                    },
                });
            })
            .catch((ex) => {
                logError(ex);
                UiNotification.showException(i18n("elements.articleOverview.failedLoading"));
                this.$categories.empty();
            });
    }

    private renderCategory(model: ISortedCategory) {
        const category = $('<div class="category open" />').data("model", model);

        const head = $('<div class="head" />').appendTo(category);
        $('<div class="collappser"><i class="la la-angle-down"></i></div>')
            .appendTo(head)
            .click((e) => {
                const clCat = $(e.currentTarget).parents(".category");
                if (clCat.hasClass("open")) this.closeCategory(clCat);
                else this.openCategory(clCat);
            });

        if (model.isLinked) {
            head.append(
                `<div class="name">${Format.htmlEscape(translate(model.category.name))}${
                    model.category.isAvailable
                        ? ""
                        : '<i class="fa fa-ban" title="' + i18n("elements.articleOverview.notAvailable") + '"></i>'
                }</div>`
            );
        } else {
            head.append(
                `<a class="name" href="/offercategory/${model.category.id}">${Format.htmlEscape(translate(model.category.name))}${
                    model.category.isAvailable
                        ? ""
                        : '<i class="fa fa-ban" title="' + i18n("elements.articleOverview.notAvailable") + '"></i>'
                }</a>`
            );
        }
        if (!model.category.isAvailable) category.addClass("unavailable");

        head.append(`<div class="count">${i18n("general.entities.item")}: ${model.articles.length}</div>`);
        $('<div class="draghandle"><i class="la la-bars"></i></div>').appendTo(head);

        const articles = $('<div class="articles" />').appendTo(category);
        for (const i in model.articles) {
            this.renderArticle(model.articles[i]).appendTo(articles);
        }

        if (!model.isLinked) {
            const articleSorter = Sortable.create(articles[0], {
                handle: ".draghandle",
                ghostClass: "dragging",
                animation: 150,
                draggable: ".article",
                onEnd: (evt) => {
                    if (evt.newIndex === evt.oldIndex) return;

                    this.onMoveArticle($(evt.item), evt.newIndex!);
                },
            });

            category.data("sorter", articleSorter);
        }
        return category;
    }

    private renderArticle(model: ISortedArticle): JQuery {
        const article = $('<div class="article" />').data("model", model);
        if (!model.article.isAvailable) article.addClass("unavailable");

        if (model.isLinked) {
            article.append(
                `<div class="name">${Format.htmlEscape(translate(model.article.name))}${model.article.isAvailable ? "" : '<i class="fa fa-ban" title="' + i18n("elements.articleOverview.notAvailable") + '"></i>'}</a>`
            );
        } else {
            article.append(
                '<a class="name" href="/article/' +
                    model.article.id +
                    '">' +
                    Format.htmlEscape(translate(model.article.name)) +
                    (model.article.isAvailable
                        ? ""
                        : '<i class="fa fa-ban" title="' + i18n("elements.articleOverview.notAvailable") + '"></i>') +
                    "</a>"
            );
        }

        const standardPrice = model.article.isOpenPriceEnabled
            ? i18n("elements.articleOverview.open")
            : toMoneyWithSign(model.article.price, true);
        article.append(`<div class="price">${standardPrice}</div>`);

        if (!model.isLinked) {
            $('<div class="draghandle"><i class="la la-bars"></i></div>').appendTo(article);
        }

        return article;
    }

    private openCategory(el: JQuery) {
        el.addClass("open");
        el.find("> .articles").stop(true, false).slideDown("fast", "easeOutExpo");
    }

    private closeCategory(el: JQuery) {
        el.removeClass("open");
        el.find("> .articles").stop(true, false).slideUp("fast", "easeOutExpo");
    }

    private onMoveCategory($category: JQuery, newIndex: number) {
        $category.addClass("saving");

        const m: ISortedCategory = $category.data("model");
        this.categorySortable.option("disabled", true);
        this.dao
            .setCategoryPosition(this.offerId, m.category.id, newIndex, m.id)
            .then(() => {
                m.order = newIndex;

                $category.removeClass("saving");
                this.categorySortable.option("disabled", false);
            })
            .catch((ex) => {
                logError(ex);
                $category.removeClass("saving");
                UiNotification.showException(i18n("elements.articleOverview.failedSort"));
            });
    }

    private onMoveArticle($article: JQuery, newIndex: number) {
        $article.addClass("saving");
        const $category = $article.parents(".category");
        const sorter = $category.data("sorter");

        const m: ISortedArticle = $article.data("model");
        const category: ISortedCategory = $category.data("model");

        sorter.option("disabled", true);
        this.dao
            .setArticleCategoryPosition(this.offerId, category.category.id, m.article.id, m.id, newIndex)
            .then(() => {
                m.order = newIndex;
                $article.removeClass("saving");
                sorter.option("disabled", false);
            })
            .catch((ex) => {
                logError(ex);
                $article.removeClass("saving");
                UiNotification.showException(i18n("elements.articleOverview.failedSort"));
            });
    }
}
